import {getPlaceholderSnapStrategy} from 'components/customer/RoomPlanner/helpers/snapping/strategies/placeholders';
import {getUnderCabinetSnap} from 'components/customer/RoomPlanner/helpers/snapping/strategies/underCabinet';
import {
    getKickPanelJointSnap,
    getLShapeKickPanelSnap,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/kickPanels';
import * as THREE from 'three';
import {describePair} from 'components/customer/RoomPlanner/helpers/snapping/context';
import {isSameLayoutItem} from 'components/customer/RoomPlanner/helpers/overlap';
import {
    isSnapCandidate,
    PairFacts,
    ProductFacts,
    SnapCandidate,
    SnapContext,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';

// Product snapping (CO-5855): the product-specific rules, which name the exact
// place a product belongs against another, as a table of routes read top to
// bottom. A pair no rule takes is left to corner and face snapping.

/**
 * One product-specific snapping rule (CO-5855): the pairs it takes, and the
 * strategy that snaps them, or none when the pair does not snap at all. A
 * pair no rule takes snaps by its faces (CO-5853) and corners (CO-5854).
 */
export interface SnapRoute {
    name: string;
    when: (pair: PairFacts) => boolean;
    strategy: SnapStrategy | null;
}

// Kicker frames and kick-length panels rest under a cabinet (R-12, CO-5716):
// a kicker frame under any cabinet that has a bottom and isn't itself a floor
// product, and a kick-length panel under anything but an L-shaped corner.
// Neither goes under a panel or filler, which they meet by their faces, as
// filler snapping met them before CO-5853. Product 353 is a non-manual panel
// but not a kick-length product, so it snaps by its faces.
const restsUnderCabinet = ({selected, other}: PairFacts) =>
    !other.isFiller &&
    ((selected.isFloorProduct &&
        !selected.isNonManualPanel &&
        !other.group.userData.noBottom &&
        !other.isFloorProduct) ||
        (selected.isKickLength && !other.isCornerLShape));

/**
 * The product rules, read top to bottom: the first row whose condition holds
 * takes the pair. A new rule is a new row, placed above the rows it must take
 * pairs from.
 */
export const SNAP_ROUTES: readonly SnapRoute[] = [
    {
        name: 'otherIgnoresSnapping',
        when: ({other}) => other.ignoresSnapping,
        strategy: null,
    },
    // Appliance GLBs snap by mesh AABB. Their pivot is centred, so the
    // cabinet strategies' corner-origin math overlaps the target.
    {
        name: 'placeholder',
        when: ({selected, other}) =>
            selected.isPlaceholder || other.isPlaceholder,
        strategy: getPlaceholderSnapStrategy,
    },
    // A kick-length panel snaps at a right angle to the end of another one,
    // and into the inside corner of an L-shaped corner base.
    {
        name: 'kickPanelJoint',
        when: ({selected, other}) =>
            selected.isKickLength && other.isKickLength && !other.isUpper,
        strategy: getKickPanelJointSnap,
    },
    {
        name: 'kickPanelIntoLCorner',
        when: ({selected, other}) =>
            selected.isKickLength && other.isCornerLShape && !other.isUpper,
        strategy: getLShapeKickPanelSnap,
    },
    {
        name: 'kickPanelBesideFloorProduct',
        when: ({selected, other}) => selected.isKickLength && other.sitsOnFloor,
        strategy: null,
    },
    // A kicker frame and adjustable legs don't go together.
    {
        name: 'kickerFrameOnLegs',
        when: ({selected, other}) => selected.isFloorProduct && other.hasLegs,
        strategy: null,
    },
    // Kicker frames and kick-length panels are measured in plan, so an upper
    // overhead would otherwise be in range.
    {
        name: 'floorProductUnderUpper',
        when: ({selected, other}) =>
            (selected.isFloorProduct || selected.isKickLength) && other.isUpper,
        strategy: null,
    },
    {
        name: 'underCabinet',
        when: restsUnderCabinet,
        strategy: getUnderCabinetSnap,
    },
];

/**
 * @param {PairFacts} pair The dragged product and one other.
 * @return {SnapRoute | null} The product rule that takes the pair, or null
 * when none does and it snaps by faces and corners.
 */
export const findSnapRoute = (pair: PairFacts): SnapRoute | null =>
    SNAP_ROUTES.find(({when}) => when(pair)) ?? null;

/**
 * One other product, paired with the dragged product and routed.
 */
export interface RoutedProduct {
    other: THREE.Group;
    pair: PairFacts;
    route: SnapRoute | null;
}

/**
 * Routes the dragged product against every other product in the room. A
 * dragged product that ignores snapping is routed against nothing. The
 * product itself is skipped (isSameLayoutItem: a cabinet and a placeholder
 * may share an id).
 * @param {SnapContext} ctx The calculation: the dragged product's facts and
 * every product in the room.
 * @return {RoutedProduct[]} Each other product with its pair and rule.
 */
export const routeProducts = (ctx: SnapContext): RoutedProduct[] =>
    ctx.selected.ignoresSnapping
        ? []
        : (ctx.productsRef ?? [])
              .filter((other) => !isSameLayoutItem(other, ctx.selected.group))
              .map((other) => {
                  const pair = describePair(ctx, other);

                  return {other, pair, route: findSnapRoute(pair)};
              });

/**
 * A product rule's snap: where it puts the product, how far that is from the
 * drag target, and the rule's name.
 */
export interface RuleSnap extends SnapCandidate {
    route: string;
    // The product the rule snapped to.
    other: ProductFacts;
}

/**
 * The nearest snap any product rule offers for one move, and the products no
 * rule takes, which snap by their corners and faces instead. A product a
 * rule takes without a strategy snaps by nothing.
 * @param {SnapContext} ctx The calculation.
 * @return {object} The nearest rule's snap, or null, and the products left
 * to corner and face snapping.
 */
export const findProductSnap = (
    ctx: SnapContext
): {snap: RuleSnap | null; unclaimed: ProductFacts[]} => {
    let snap: RuleSnap | null = null;
    const unclaimed: ProductFacts[] = [];

    routeProducts(ctx).forEach(({pair, route}) => {
        if (route === null) {
            unclaimed.push(pair.other);
            return;
        }
        if (route.strategy === null) return;

        const outcome = route.strategy(ctx, pair);
        if (!isSnapCandidate(outcome)) return;
        if (snap !== null && outcome.distance >= snap.distance) return;

        snap = {
            position: outcome.position,
            distance: outcome.distance,
            route: route.name,
            other: pair.other,
        };
    });

    return {snap, unclaimed};
};
